import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  LayoutDashboard, Map, Image, MessageSquare, LogOut,
  Plus, Trash2, Edit2, X, Check, Building2, Menu, RefreshCw, Upload,
  Grid3X3, FileText, Search, Settings, Eye, EyeOff, Globe, BarChart3, Save,
  TrendingUp, Flame, Zap, Snowflake, Info, ChevronDown, ChevronUp,
  CreditCard, Camera, IndianRupee, Lock, Download, MapPinned, Wand2, Star
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getAuthHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem("admin_token")}` };
}

const STATUS_COLORS = {
  available: "bg-emerald-100 text-emerald-700",
  sold: "bg-slate-100 text-slate-600",
  reserved: "bg-amber-100 text-amber-700",
};

const ENQ_COLORS = {
  new: "bg-blue-100 text-blue-700",
  contacted: "bg-orange-100 text-orange-700",
  closed: "bg-slate-100 text-slate-500",
};

function StatCard({ label, value, color }) {
  return (
    <div className="bg-white border border-slate-200 p-5">
      <div className={`text-3xl font-bold mb-1 ${color}`} style={{ fontFamily: "'JetBrains Mono', monospace" }}>
        {value}
      </div>
      <div className="text-slate-500 text-sm">{label}</div>
    </div>
  );
}

/* Score Ring – SVG donut chart for lead score */
function ScoreRing({ score, max = 100, size = 44, stroke = 4, category }) {
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const pct = Math.min(score / max, 1);
  const colors = { hot: "#ef4444", warm: "#f59e0b", cold: "#3b82f6" };
  const glows = { hot: "drop-shadow(0 0 4px #ef444480)", warm: "drop-shadow(0 0 4px #f59e0b60)", cold: "none" };
  return (
    <svg width={size} height={size} style={{ filter: glows[category] || "none" }}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e2e8f0" strokeWidth={stroke} />
      <circle
        cx={size / 2} cy={size / 2} r={r} fill="none"
        stroke={colors[category] || "#94a3b8"} strokeWidth={stroke}
        strokeDasharray={circ} strokeDashoffset={circ * (1 - pct)}
        strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
        style={{ transition: "stroke-dashoffset 0.8s ease" }}
      />
      <text x="50%" y="50%" textAnchor="middle" dominantBaseline="central" fill={colors[category] || "#64748b"}
        fontSize={size * 0.28} fontWeight="800" fontFamily="'JetBrains Mono', monospace">
        {score}
      </text>
    </svg>
  );
}

/* Glassmorphism Lead Card */
function LeadStatCard({ label, value, icon: Icon, gradient, glow }) {
  return (
    <div
      className="relative overflow-hidden rounded-lg border p-5"
      style={{
        background: gradient,
        borderColor: "rgba(255,255,255,0.15)",
        boxShadow: glow ? `0 4px 24px ${glow}` : "none",
      }}
    >
      <div className="flex items-center justify-between mb-2">
        <Icon className="w-5 h-5 text-white/60" />
        <span className="text-3xl font-extrabold text-white" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{value}</span>
      </div>
      <div className="text-white/70 text-xs font-bold uppercase tracking-wider">{label}</div>
      {/* decorative circle */}
      <div className="absolute -bottom-4 -right-4 w-20 h-20 rounded-full opacity-10 bg-white" />
    </div>
  );
}

/* Pipeline bar */
function PipelineBar({ hot, warm, cold }) {
  const total = hot + warm + cold || 1;
  return (
    <div className="w-full">
      <div className="flex h-3 rounded-full overflow-hidden bg-slate-200">
        {hot > 0 && <div className="bg-gradient-to-r from-red-500 to-orange-400" style={{ width: `${(hot / total) * 100}%`, transition: "width 0.6s ease" }} />}
        {warm > 0 && <div className="bg-gradient-to-r from-amber-400 to-yellow-300" style={{ width: `${(warm / total) * 100}%`, transition: "width 0.6s ease" }} />}
        {cold > 0 && <div className="bg-gradient-to-r from-blue-400 to-sky-300" style={{ width: `${(cold / total) * 100}%`, transition: "width 0.6s ease" }} />}
      </div>
      <div className="flex justify-between mt-1.5 text-xs font-bold">
        <span className="text-red-500">{hot} Hot ({total ? Math.round((hot / total) * 100) : 0}%)</span>
        <span className="text-amber-500">{warm} Warm ({total ? Math.round((warm / total) * 100) : 0}%)</span>
        <span className="text-blue-500">{cold} Cold ({total ? Math.round((cold / total) * 100) : 0}%)</span>
      </div>
    </div>
  );
}

/* Score Breakdown Tooltip */
function ScoreBreakdown({ breakdown }) {
  const labels = { plot_size: "Plot Size", industry: "Industry", otp_verified: "OTP Verified", repeat_visits: "Repeat Visits", recency: "Recency" };
  const maxes = { plot_size: 30, industry: 25, otp_verified: 15, repeat_visits: 10, recency: 20 };
  const colors = { plot_size: "#f97316", industry: "#8b5cf6", otp_verified: "#10b981", repeat_visits: "#3b82f6", recency: "#ec4899" };
  return (
    <div className="space-y-1.5 min-w-[180px]">
      {Object.entries(breakdown || {}).map(([k, v]) => (
        <div key={k}>
          <div className="flex justify-between text-xs mb-0.5">
            <span className="text-slate-600">{labels[k] || k}</span>
            <span className="font-bold" style={{ fontFamily: "'JetBrains Mono', monospace", color: colors[k] || "#64748b" }}>{v}/{maxes[k] || "?"}</span>
          </div>
          <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full rounded-full" style={{ width: `${(v / (maxes[k] || 30)) * 100}%`, background: colors[k] || "#94a3b8", transition: "width 0.5s ease" }} />
          </div>
        </div>
      ))}
    </div>
  );
}

const PLOT_SIZES = [500, 1000, 2000, 3000, 5000, 10000];

const DEFAULT_PLOT_FORM = {
  size: 500, price_per_sqmtr: 7500, status: "available",
  features: "", image_url: "", description: ""
};

const DEFAULT_GALLERY_FORM = {
  title: "", image_url: "", category: "industry"
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [stats, setStats] = useState({});
  const [plots, setPlots] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [plotForm, setPlotForm] = useState(DEFAULT_PLOT_FORM);
  const [editingPlot, setEditingPlot] = useState(null);
  const [showPlotForm, setShowPlotForm] = useState(false);
  const [galleryForm, setGalleryForm] = useState(DEFAULT_GALLERY_FORM);
  const [showGalleryForm, setShowGalleryForm] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [sitePlots, setSitePlots] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [blogForm, setBlogForm] = useState({ title: "", content: "", excerpt: "", image_url: "", category: "news", published: true });
  const [showBlogForm, setShowBlogForm] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);
  const [seoList, setSeoList] = useState([]);
  const [seoForm, setSeoForm] = useState({ page: "home", title: "", description: "", keywords: "", og_image: "" });
  const [showSeoForm, setShowSeoForm] = useState(false);
  const [socialLinks, setSocialLinks] = useState({ facebook: "", instagram: "", twitter: "", linkedin: "", youtube: "" });
  const [analyticsForm, setAnalyticsForm] = useState({ ga_measurement_id: "" });
  const [leads, setLeads] = useState([]);
  const [leadsFilter, setLeadsFilter] = useState("all");
  const [expandedLead, setExpandedLead] = useState(null);
  const [recalculating, setRecalculating] = useState(false);
  const [followUps, setFollowUps] = useState([]);
  const [followUpStats, setFollowUpStats] = useState({});
  const [processingFollowUps, setProcessingFollowUps] = useState(false);
  const [leadsView, setLeadsView] = useState("leads"); // "leads" | "followups"
  const [payments, setPayments] = useState([]);
  const [paymentStats, setPaymentStats] = useState({});
  const [tourScenes, setTourScenes] = useState([]);
  const [tourForm, setTourForm] = useState({ title: "", description: "", image_url: "", order: 0 });
  const [showTourForm, setShowTourForm] = useState(false);
  const [editingTour, setEditingTour] = useState(null);
  const [credForm, setCredForm] = useState({ current_password: "", new_username: "", new_password: "", confirm_password: "" });
  const [credSaving, setCredSaving] = useState(false);
  const [adminUsername, setAdminUsername] = useState("");
  const [generatingBlog, setGeneratingBlog] = useState(false);
  const [reviews, setReviews] = useState([]);

  const fetchData = useCallback(async () => {
    const headers = getAuthHeaders();
    try {
      const [statsRes, plotsRes, galleryRes, enquiriesRes, bookingsRes, sitePlotsRes, blogRes, seoRes, socialRes, analyticsRes, leadsRes, fuRes, fuStatsRes, paymentsRes, paymentStatsRes, tourRes, reviewsRes] = await Promise.all([
        axios.get(`${API}/stats`, { headers }),
        axios.get(`${API}/plots`, { headers }),
        axios.get(`${API}/gallery`, { headers }),
        axios.get(`${API}/enquiries`, { headers }),
        axios.get(`${API}/bookings`, { headers }),
        axios.get(`${API}/site-plots`),
        axios.get(`${API}/blog?published_only=false`, { headers }),
        axios.get(`${API}/seo`, { headers }),
        axios.get(`${API}/settings/social-links`),
        axios.get(`${API}/settings/analytics`),
        axios.get(`${API}/leads`, { headers }),
        axios.get(`${API}/follow-ups`, { headers }),
        axios.get(`${API}/follow-ups/stats`, { headers }),
        axios.get(`${API}/payments`, { headers }),
        axios.get(`${API}/payments/stats`, { headers }),
        axios.get(`${API}/virtual-tour/scenes`),
        axios.get(`${API}/admin/reviews`, { headers }),
      ]);
      setStats(statsRes.data);
      setPlots(plotsRes.data);
      setGallery(galleryRes.data);
      setEnquiries(enquiriesRes.data);
      setBookings(bookingsRes.data);
      setSitePlots(sitePlotsRes.data);
      setBlogPosts(blogRes.data);
      setSeoList(seoRes.data);
      if (socialRes.data && Object.keys(socialRes.data).length) setSocialLinks(socialRes.data);
      if (analyticsRes.data && Object.keys(analyticsRes.data).length) setAnalyticsForm(analyticsRes.data);
      setLeads(leadsRes.data);
      setFollowUps(fuRes.data);
      setFollowUpStats(fuStatsRes.data);
      setPayments(paymentsRes.data);
      setPaymentStats(paymentStatsRes.data);
      setTourScenes(tourRes.data);
      setReviews(reviewsRes.data);
      // Fetch admin profile
      try {
        const profileRes = await axios.get(`${API}/auth/profile`, { headers });
        setAdminUsername(profileRes.data.username);
      } catch {}
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("admin_token");
        navigate("/admin");
      }
    }
  }, [navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_username");
    navigate("/admin");
  };

  // Plots
  const savePlot = async () => {
    const headers = getAuthHeaders();
    const payload = {
      ...plotForm,
      size: Number(plotForm.size),
      price_per_sqmtr: Number(plotForm.price_per_sqmtr),
      features: plotForm.features ? plotForm.features.split(",").map((f) => f.trim()).filter(Boolean) : [],
    };
    try {
      if (editingPlot) {
        await axios.put(`${API}/plots/${editingPlot.id}`, payload, { headers });
        toast.success("Plot updated");
      } else {
        await axios.post(`${API}/plots`, payload, { headers });
        toast.success("Plot added");
      }
      setShowPlotForm(false);
      setEditingPlot(null);
      setPlotForm(DEFAULT_PLOT_FORM);
      fetchData();
    } catch {
      toast.error("Failed to save plot");
    }
  };

  const deletePlot = async (id) => {
    if (!window.confirm("Delete this plot?")) return;
    try {
      await axios.delete(`${API}/plots/${id}`, { headers: getAuthHeaders() });
      toast.success("Plot deleted");
      fetchData();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const startEditPlot = (plot) => {
    setEditingPlot(plot);
    setPlotForm({
      size: plot.size,
      price_per_sqmtr: plot.price_per_sqmtr,
      status: plot.status,
      features: (plot.features || []).join(", "),
      image_url: plot.image_url || "",
      description: plot.description || "",
    });
    setShowPlotForm(true);
  };

  // Gallery
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ["image/jpeg", "image/png", "image/gif", "image/webp"];
    if (!allowed.includes(file.type)) {
      toast.error("Only JPG, PNG, GIF, WebP allowed");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File too large. Max 10MB.");
      return;
    }
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/upload/image`, formData, {
        headers: { ...getAuthHeaders(), "Content-Type": "multipart/form-data" },
      });
      const imageUrl = `${process.env.REACT_APP_BACKEND_URL}/api/files/${res.data.storage_path}`;
      setGalleryForm({ ...galleryForm, image_url: imageUrl });
      toast.success("Image uploaded!");
    } catch (err) {
      toast.error("Upload failed. Try using a URL instead.");
    } finally {
      setUploadingImage(false);
    }
  };

  const saveGallery = async () => {
    if (!galleryForm.title || !galleryForm.image_url) {
      toast.error("Title and image (URL or upload) are required");
      return;
    }
    try {
      await axios.post(`${API}/gallery`, galleryForm, { headers: getAuthHeaders() });
      toast.success("Image added");
      setGalleryForm(DEFAULT_GALLERY_FORM);
      setShowGalleryForm(false);
      fetchData();
    } catch {
      toast.error("Failed to add image");
    }
  };

  const deleteGallery = async (id) => {
    if (!window.confirm("Delete this image?")) return;
    try {
      await axios.delete(`${API}/gallery/${id}`, { headers: getAuthHeaders() });
      toast.success("Image deleted");
      fetchData();
    } catch {
      toast.error("Failed to delete");
    }
  };

  // Enquiries
  const updateEnquiryStatus = async (id, status) => {
    try {
      await axios.put(`${API}/enquiries/${id}/status`, { status }, { headers: getAuthHeaders() });
      toast.success("Status updated");
      fetchData();
    } catch {
      toast.error("Failed to update");
    }
  };

  const deleteEnquiry = async (id) => {
    if (!window.confirm("Delete this enquiry?")) return;
    try {
      await axios.delete(`${API}/enquiries/${id}`, { headers: getAuthHeaders() });
      toast.success("Enquiry deleted");
      fetchData();
    } catch {
      toast.error("Failed to delete");
    }
  };

  // Bookings
  const updateBookingStatus = async (id, status) => {
    try {
      await axios.put(`${API}/bookings/${id}/status`, { status }, { headers: getAuthHeaders() });
      toast.success("Status updated");
      fetchData();
    } catch {
      toast.error("Failed to update");
    }
  };

  const deleteBooking = async (id) => {
    if (!window.confirm("Delete this booking?")) return;
    try {
      await axios.delete(`${API}/bookings/${id}`, { headers: getAuthHeaders() });
      toast.success("Booking deleted");
      fetchData();
    } catch {
      toast.error("Failed to delete");
    }
  };

  // Reviews
  const updateReviewStatus = async (id, status) => {
    try {
      await axios.put(`${API}/reviews/${id}/status`, { status }, { headers: getAuthHeaders() });
      toast.success(status === "approved" ? "Review approved & published" : "Review updated");
      fetchData();
    } catch {
      toast.error("Failed to update review");
    }
  };

  const deleteReview = async (id) => {
    if (!window.confirm("Delete this review?")) return;
    try {
      await axios.delete(`${API}/reviews/${id}`, { headers: getAuthHeaders() });
      toast.success("Review deleted");
      fetchData();
    } catch {
      toast.error("Failed to delete");
    }
  };

  // Site Plot Status
  const updateSitePlotStatus = async (plotId, status) => {
    try {
      await axios.patch(`${API}/site-plots/${plotId}/status`, { status }, { headers: getAuthHeaders() });
      toast.success(`Plot ${plotId} updated to ${status}`);
      fetchData();
    } catch {
      toast.error("Failed to update plot status");
    }
  };

  // Blog
  const saveBlog = async () => {
    if (!blogForm.title || !blogForm.content) { toast.error("Title and content required"); return; }
    const headers = getAuthHeaders();
    try {
      if (editingBlog) {
        await axios.put(`${API}/blog/${editingBlog.id}`, blogForm, { headers });
        toast.success("Post updated");
      } else {
        await axios.post(`${API}/blog`, blogForm, { headers });
        toast.success("Post created");
      }
      setShowBlogForm(false);
      setEditingBlog(null);
      setBlogForm({ title: "", content: "", excerpt: "", image_url: "", category: "news", published: true });
      fetchData();
    } catch { toast.error("Failed to save post"); }
  };

  const deleteBlog = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    try {
      await axios.delete(`${API}/blog/${id}`, { headers: getAuthHeaders() });
      toast.success("Post deleted");
      fetchData();
    } catch { toast.error("Failed to delete"); }
  };

  // SEO
  const saveSeo = async () => {
    try {
      await axios.put(`${API}/seo/${seoForm.page}`, seoForm, { headers: getAuthHeaders() });
      toast.success(`SEO for "${seoForm.page}" updated`);
      setShowSeoForm(false);
      fetchData();
    } catch { toast.error("Failed to save SEO"); }
  };

  // Social Links
  const saveSocialLinks = async () => {
    try {
      await axios.put(`${API}/settings/social-links`, socialLinks, { headers: getAuthHeaders() });
      toast.success("Social links saved");
    } catch { toast.error("Failed to save"); }
  };

  // Analytics
  const saveAnalytics = async () => {
    try {
      await axios.put(`${API}/settings/analytics`, analyticsForm, { headers: getAuthHeaders() });
      toast.success("Analytics settings saved");
    } catch { toast.error("Failed to save"); }
  };

  // Leads
  const recalculateLeads = async () => {
    setRecalculating(true);
    try {
      await axios.post(`${API}/leads/recalculate`, {}, { headers: getAuthHeaders() });
      toast.success("Lead scores recalculated!");
      fetchData();
    } catch { toast.error("Recalculation failed"); }
    finally { setRecalculating(false); }
  };

  // Follow-ups
  const processPendingFollowUps = async () => {
    setProcessingFollowUps(true);
    try {
      const res = await axios.post(`${API}/follow-ups/process-pending`, {}, { headers: getAuthHeaders() });
      toast.success(res.data.message);
      fetchData();
    } catch { toast.error("Processing failed"); }
    finally { setProcessingFollowUps(false); }
  };

  const sendSingleFollowUp = async (id) => {
    try {
      await axios.post(`${API}/follow-ups/${id}/send`, {}, { headers: getAuthHeaders() });
      toast.success("Follow-up sent!");
      fetchData();
    } catch { toast.error("Failed to send"); }
  };

  // Virtual Tour
  const saveTourScene = async () => {
    const headers = getAuthHeaders();
    const payload = { ...tourForm, order: Number(tourForm.order) };
    try {
      if (editingTour) {
        await axios.put(`${API}/virtual-tour/scenes/${editingTour.id}`, payload, { headers });
        toast.success("Scene updated");
      } else {
        await axios.post(`${API}/virtual-tour/scenes`, payload, { headers });
        toast.success("Scene added");
      }
      setShowTourForm(false);
      setEditingTour(null);
      setTourForm({ title: "", description: "", image_url: "", order: 0 });
      fetchData();
    } catch { toast.error("Failed to save scene"); }
  };

  const deleteTourScene = async (id) => {
    if (!window.confirm("Delete this scene?")) return;
    try {
      await axios.delete(`${API}/virtual-tour/scenes/${id}`, { headers: getAuthHeaders() });
      toast.success("Scene deleted");
      fetchData();
    } catch { toast.error("Failed to delete"); }
  };

  // Credentials
  const saveCredentials = async () => {
    if (!credForm.current_password) { toast.error("Enter current password"); return; }
    if (credForm.new_password && credForm.new_password !== credForm.confirm_password) { toast.error("Passwords don't match"); return; }
    if (credForm.new_password && credForm.new_password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setCredSaving(true);
    try {
      const res = await axios.post(`${API}/auth/change-credentials`, {
        current_password: credForm.current_password,
        new_username: credForm.new_username || "",
        new_password: credForm.new_password || "",
      }, { headers: getAuthHeaders() });
      toast.success(res.data.message);
      setCredForm({ current_password: "", new_username: "", new_password: "", confirm_password: "" });
      setAdminUsername(res.data.username);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update credentials");
    } finally { setCredSaving(false); }
  };

  // Auto-generate blog
  const autoGenerateBlog = async () => {
    setGeneratingBlog(true);
    try {
      const res = await axios.post(`${API}/blog/auto-generate`, {}, { headers: getAuthHeaders() });
      toast.success(`Blog post generated: "${res.data.title}"`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Blog generation failed");
    } finally { setGeneratingBlog(false); }
  };

  const navItems = [
    { id: "overview", icon: LayoutDashboard, label: "Overview" },
    { id: "leads", icon: TrendingUp, label: "Leads" },
    { id: "plots", icon: Map, label: "Plots" },
    { id: "site-plots", icon: Grid3X3, label: "Site Plots" },
    { id: "gallery", icon: Image, label: "Gallery" },
    { id: "enquiries", icon: MessageSquare, label: "Enquiries" },
    { id: "bookings", icon: Building2, label: "Bookings" },
    { id: "reviews", icon: Star, label: "Reviews" },
    { id: "payments", icon: CreditCard, label: "Payments" },
    { id: "virtual-tour", icon: Camera, label: "360° Tour" },
    { id: "blog", icon: FileText, label: "Blog" },
    { id: "seo", icon: Search, label: "SEO" },
    { id: "settings", icon: Settings, label: "Settings" },
  ];

  return (
    <div className="min-h-screen bg-slate-100 flex" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-56 bg-slate-900 transform transition-transform duration-300 flex flex-col ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        } lg:translate-x-0 lg:static lg:flex`}
      >
        {/* Sidebar Header */}
        <div className="p-4 border-b border-slate-800">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-orange-500 flex items-center justify-center">
              <Building2 className="w-4 h-4 text-white" />
            </div>
            <div>
              <div className="text-white text-sm font-bold uppercase" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                VS Infra
              </div>
              <div className="text-slate-400 text-xs">Admin Panel</div>
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 py-4 overflow-y-auto">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => { setActiveTab(item.id); setSidebarOpen(false); }}
              data-testid={`admin-nav-${item.id}`}
              className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors ${
                activeTab === item.id
                  ? "bg-orange-500/20 text-orange-400 border-l-2 border-orange-500"
                  : "text-slate-400 hover:text-white hover:bg-slate-800"
              }`}
            >
              <item.icon className="w-4 h-4" />
              {item.label}
            </button>
          ))}
        </nav>

        <div className="p-4 border-t border-slate-800">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-2 text-slate-400 hover:text-white text-sm py-2 transition-colors"
            data-testid="admin-logout-btn"
          >
            <LogOut className="w-4 h-4" /> Sign Out
          </button>
          <a href="/" className="block text-slate-500 hover:text-slate-300 text-xs mt-2 transition-colors">
            ← Back to Website
          </a>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-30 bg-black/50 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3 sticky top-0 z-20">
          <button
            className="lg:hidden text-slate-500 hover:text-slate-900"
            onClick={() => setSidebarOpen(true)}
            data-testid="admin-mobile-menu-btn"
          >
            <Menu className="w-5 h-5" />
          </button>
          <h1
            className="text-slate-900 font-bold uppercase text-lg"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
          >
            {navItems.find((n) => n.id === activeTab)?.label}
          </h1>
          <span className="ml-auto text-xs text-slate-400">
            Logged in as <span className="text-slate-700 font-medium">{localStorage.getItem("admin_username")}</span>
          </span>
        </header>

        <main className="flex-1 p-4 md:p-6 overflow-y-auto">
          {/* Overview Tab */}
          {activeTab === "overview" && (
            <div className="space-y-6" data-testid="admin-overview">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard label="Total Plots" value={stats.total_plots ?? 0} color="text-slate-800" />
                <StatCard label="Available Plots" value={stats.available_plots ?? 0} color="text-emerald-600" />
                <StatCard label="New Enquiries" value={stats.new_enquiries ?? 0} color="text-orange-500" />
                <StatCard label="New Bookings" value={stats.new_bookings ?? 0} color="text-blue-600" />
              </div>

              {/* Lead Pipeline Widget */}
              <div className="bg-white border border-slate-200 p-5" data-testid="overview-lead-pipeline">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-slate-900 text-sm uppercase tracking-wide flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-orange-500" /> Lead Pipeline
                  </h3>
                  <button onClick={() => setActiveTab("leads")} className="text-xs text-orange-500 hover:text-orange-600 font-bold uppercase tracking-wider">
                    View All →
                  </button>
                </div>
                <div className="grid grid-cols-3 gap-3 mb-4">
                  <LeadStatCard label="Hot Leads" value={stats.hot_leads ?? 0} icon={Flame}
                    gradient="linear-gradient(135deg, #dc2626 0%, #f97316 100%)" glow="rgba(239,68,68,0.25)" />
                  <LeadStatCard label="Warm Leads" value={stats.warm_leads ?? 0} icon={Zap}
                    gradient="linear-gradient(135deg, #f59e0b 0%, #eab308 100%)" glow="rgba(245,158,11,0.2)" />
                  <LeadStatCard label="Cold Leads" value={stats.cold_leads ?? 0} icon={Snowflake}
                    gradient="linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)" glow="rgba(59,130,246,0.2)" />
                </div>
                <PipelineBar hot={stats.hot_leads ?? 0} warm={stats.warm_leads ?? 0} cold={stats.cold_leads ?? 0} />
                {leads.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-slate-100">
                    <p className="text-xs text-slate-500 mb-2 font-bold uppercase tracking-wide">Top Leads</p>
                    <div className="space-y-2">
                      {leads.slice(0, 3).map((l) => (
                        <div key={l.id} className="flex items-center gap-3 text-sm">
                          <ScoreRing score={l.score} category={l.category} size={32} stroke={3} />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-slate-900 truncate">{l.name}</div>
                            <div className="text-xs text-slate-400">{l.plot_size} · {l.industry_type || "N/A"}</div>
                          </div>
                          <span className={`text-xs font-extrabold uppercase px-2 py-0.5 rounded-full ${
                            l.category === "hot" ? "bg-red-100 text-red-600" : l.category === "warm" ? "bg-amber-100 text-amber-600" : "bg-blue-100 text-blue-600"
                          }`}>{l.category}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="bg-white border border-slate-200 p-5">
                <h3 className="font-bold text-slate-900 mb-3 text-sm uppercase tracking-wide">Quick Actions</h3>
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={() => { setActiveTab("plots"); setShowPlotForm(true); setEditingPlot(null); setPlotForm(DEFAULT_PLOT_FORM); }}
                    className="flex items-center gap-2 bg-slate-900 text-white px-4 py-2 text-sm font-semibold hover:bg-slate-700 transition-colors"
                  >
                    <Plus className="w-4 h-4" /> Add Plot
                  </button>
                  <button
                    onClick={() => { setActiveTab("gallery"); setShowGalleryForm(true); }}
                    className="flex items-center gap-2 bg-orange-500 text-white px-4 py-2 text-sm font-semibold hover:bg-orange-600 transition-colors"
                  >
                    <Plus className="w-4 h-4" /> Add Gallery Image
                  </button>
                  <button
                    onClick={() => setActiveTab("enquiries")}
                    className="flex items-center gap-2 border border-slate-300 text-slate-700 px-4 py-2 text-sm font-semibold hover:border-orange-400 transition-colors"
                  >
                    <MessageSquare className="w-4 h-4" /> View Enquiries ({stats.new_enquiries ?? 0} new)
                  </button>
                  <button
                    onClick={() => setActiveTab("bookings")}
                    className="flex items-center gap-2 border border-slate-300 text-slate-700 px-4 py-2 text-sm font-semibold hover:border-blue-400 transition-colors"
                  >
                    <Building2 className="w-4 h-4" /> View Bookings ({stats.new_bookings ?? 0} new)
                  </button>
                  <button
                    onClick={async () => {
                      const headers = getAuthHeaders();
                      try {
                        await axios.post(`${API}/schemes/refresh`, {}, { headers });
                        toast.success("Scheme refresh triggered! Updates will appear shortly.");
                      } catch { toast.error("Refresh failed"); }
                    }}
                    className="flex items-center gap-2 border border-emerald-300 text-emerald-700 px-4 py-2 text-sm font-semibold hover:bg-emerald-50 transition-colors"
                    data-testid="admin-refresh-schemes-btn"
                  >
                    <RefreshCw className="w-4 h-4" /> Refresh Schemes from Govt Sites
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Leads Tab — Modern Glassmorphism UI */}
          {activeTab === "leads" && (() => {
            const hotLeads = leads.filter(l => l.category === "hot");
            const warmLeads = leads.filter(l => l.category === "warm");
            const coldLeads = leads.filter(l => l.category === "cold");
            const filtered = leadsFilter === "all" ? leads : leads.filter(l => l.category === leadsFilter);
            return (
              <div className="space-y-5" data-testid="admin-leads">
                {/* Glassmorphism Summary Cards */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <LeadStatCard label="Total Leads" value={leads.length} icon={TrendingUp}
                    gradient="linear-gradient(135deg, #1e293b 0%, #334155 100%)" glow="rgba(30,41,59,0.3)" />
                  <LeadStatCard label="Hot Leads" value={hotLeads.length} icon={Flame}
                    gradient="linear-gradient(135deg, #dc2626 0%, #f97316 100%)" glow="rgba(239,68,68,0.3)" />
                  <LeadStatCard label="Warm Leads" value={warmLeads.length} icon={Zap}
                    gradient="linear-gradient(135deg, #f59e0b 0%, #eab308 100%)" glow="rgba(245,158,11,0.25)" />
                  <LeadStatCard label="Cold Leads" value={coldLeads.length} icon={Snowflake}
                    gradient="linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)" glow="rgba(59,130,246,0.25)" />
                </div>

                {/* Pipeline Distribution */}
                <div className="bg-white border border-slate-200 p-5 rounded-lg">
                  <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wide mb-3">Lead Pipeline Distribution</h3>
                  <PipelineBar hot={hotLeads.length} warm={warmLeads.length} cold={coldLeads.length} />
                </div>

                {/* View Toggle: Leads | Follow-ups */}
                <div className="flex items-center gap-2 border-b border-slate-200 pb-2">
                  <button onClick={() => setLeadsView("leads")} data-testid="leads-view-toggle-leads"
                    className={`px-4 py-2 text-sm font-bold uppercase tracking-wider transition-colors ${leadsView === "leads" ? "text-orange-600 border-b-2 border-orange-500" : "text-slate-400 hover:text-slate-600"}`}>
                    Leads ({leads.length})
                  </button>
                  <button onClick={() => setLeadsView("followups")} data-testid="leads-view-toggle-followups"
                    className={`px-4 py-2 text-sm font-bold uppercase tracking-wider transition-colors ${leadsView === "followups" ? "text-orange-600 border-b-2 border-orange-500" : "text-slate-400 hover:text-slate-600"}`}>
                    Follow-ups ({followUpStats.total || 0})
                  </button>
                </div>

                {leadsView === "leads" && (<>
                {/* Filter + Recalculate */}
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex gap-2">
                    {[
                      { key: "all", label: "All", cls: "border-slate-400 text-slate-700 bg-slate-50" },
                      { key: "hot", label: "Hot", cls: "border-red-400 text-red-600 bg-red-50" },
                      { key: "warm", label: "Warm", cls: "border-amber-400 text-amber-600 bg-amber-50" },
                      { key: "cold", label: "Cold", cls: "border-blue-400 text-blue-600 bg-blue-50" },
                    ].map(f => (
                      <button key={f.key} onClick={() => setLeadsFilter(f.key)} data-testid={`leads-filter-${f.key}`}
                        className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider border rounded-full transition-all ${
                          leadsFilter === f.key ? f.cls + " ring-2 ring-offset-1 ring-current" : "border-slate-200 text-slate-400 hover:border-slate-400"
                        }`}>
                        {f.label} {f.key !== "all" && `(${f.key === "hot" ? hotLeads.length : f.key === "warm" ? warmLeads.length : coldLeads.length})`}
                      </button>
                    ))}
                  </div>
                  <button onClick={recalculateLeads} disabled={recalculating}
                    className="flex items-center gap-1.5 px-4 py-2 text-xs font-bold uppercase tracking-wider bg-slate-900 text-white hover:bg-slate-700 transition-colors rounded-md disabled:opacity-50"
                    data-testid="leads-recalculate-btn">
                    <RefreshCw className={`w-3.5 h-3.5 ${recalculating ? "animate-spin" : ""}`} />
                    {recalculating ? "Calculating..." : "Recalculate Scores"}
                  </button>
                </div>

                {/* Leads Table */}
                <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200">
                        <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Score</th>
                        <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Lead</th>
                        <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500 hidden md:table-cell">Contact</th>
                        <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500 hidden lg:table-cell">Plot / Industry</th>
                        <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Category</th>
                        <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500 hidden lg:table-cell">Source</th>
                        <th className="text-right px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Breakdown</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((lead) => (
                        <React.Fragment key={`${lead.source}-${lead.id}`}>
                          <tr className={`border-b border-slate-100 hover:bg-slate-50 transition-colors cursor-pointer ${
                            lead.category === "hot" ? "bg-red-50/30" : ""
                          }`} onClick={() => setExpandedLead(expandedLead === lead.id ? null : lead.id)}
                            data-testid={`leads-row-${lead.id}`}>
                            <td className="px-4 py-3">
                              <ScoreRing score={lead.score} category={lead.category} size={40} stroke={3.5} />
                            </td>
                            <td className="px-4 py-3">
                              <div className="font-bold text-slate-900">{lead.name}</div>
                              {lead.company_name && <div className="text-xs text-slate-400">{lead.company_name}</div>}
                              <div className="text-xs text-slate-400 md:hidden">{lead.phone}</div>
                            </td>
                            <td className="px-4 py-3 hidden md:table-cell">
                              <div className="text-slate-700 text-sm">{lead.phone}</div>
                              <div className="text-xs text-slate-400">{lead.email}</div>
                            </td>
                            <td className="px-4 py-3 hidden lg:table-cell">
                              <div className="text-orange-600 font-semibold text-xs" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{lead.plot_size || "—"}</div>
                              <div className="text-slate-500 text-xs">{lead.industry_type || "—"}</div>
                            </td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex items-center gap-1 text-xs font-extrabold uppercase px-2.5 py-1 rounded-full ${
                                lead.category === "hot" ? "bg-gradient-to-r from-red-100 to-orange-100 text-red-600"
                                : lead.category === "warm" ? "bg-gradient-to-r from-amber-100 to-yellow-100 text-amber-600"
                                : "bg-gradient-to-r from-blue-100 to-sky-100 text-blue-600"
                              }`}>
                                {lead.category === "hot" ? <Flame className="w-3 h-3" /> : lead.category === "warm" ? <Zap className="w-3 h-3" /> : <Snowflake className="w-3 h-3" />}
                                {lead.category}
                              </span>
                            </td>
                            <td className="px-4 py-3 hidden lg:table-cell">
                              <span className={`text-xs font-bold px-2 py-0.5 rounded ${
                                lead.source === "booking" ? "bg-purple-100 text-purple-600" : "bg-teal-100 text-teal-600"
                              }`}>{lead.source}</span>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <button className="text-slate-400 hover:text-slate-600 transition-colors" data-testid={`leads-expand-${lead.id}`}>
                                {expandedLead === lead.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                              </button>
                            </td>
                          </tr>
                          {expandedLead === lead.id && (
                            <tr className="bg-slate-50">
                              <td colSpan={7} className="px-4 py-4">
                                <div className="flex flex-col md:flex-row gap-6">
                                  <div className="flex-1">
                                    <p className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-2 flex items-center gap-1"><Info className="w-3 h-3" /> Score Breakdown</p>
                                    <ScoreBreakdown breakdown={lead.breakdown} />
                                  </div>
                                  <div className="flex-1 text-sm text-slate-600 space-y-1">
                                    <p><span className="text-slate-400">Status:</span> <span className="font-bold capitalize">{lead.status}</span></p>
                                    <p><span className="text-slate-400">Date:</span> {lead.created_at?.slice(0, 10)}</p>
                                    <p><span className="text-slate-400">Email:</span> {lead.email || "—"}</p>
                                    <p><span className="text-slate-400">Phone:</span> {lead.phone || "—"}</p>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                      {filtered.length === 0 && (
                        <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-400">
                          {leads.length === 0 ? "No leads yet. Bookings and enquiries will appear here with auto-calculated scores." : "No leads match this filter."}
                        </td></tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Scoring Legend */}
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-xs text-slate-500">
                  <p className="font-bold text-slate-700 uppercase tracking-wide mb-2">Scoring Criteria (0–100)</p>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <div><span className="inline-block w-2 h-2 rounded-full bg-orange-500 mr-1"></span>Plot Size <span className="text-slate-400">(0-30)</span></div>
                    <div><span className="inline-block w-2 h-2 rounded-full bg-purple-500 mr-1"></span>Industry Type <span className="text-slate-400">(0-25)</span></div>
                    <div><span className="inline-block w-2 h-2 rounded-full bg-emerald-500 mr-1"></span>OTP Verified <span className="text-slate-400">(0-15)</span></div>
                    <div><span className="inline-block w-2 h-2 rounded-full bg-blue-500 mr-1"></span>Repeat Visits <span className="text-slate-400">(0-10)</span></div>
                    <div><span className="inline-block w-2 h-2 rounded-full bg-pink-500 mr-1"></span>Recency <span className="text-slate-400">(0-20)</span></div>
                  </div>
                </div>
                </>)}

                {/* Follow-ups View */}
                {leadsView === "followups" && (
                  <div className="space-y-4" data-testid="admin-followups">
                    {/* Follow-up Stats Cards */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {[
                        { label: "Pending", value: followUpStats.pending || 0, bg: "bg-amber-50 border-amber-200", text: "text-amber-700" },
                        { label: "Sent", value: followUpStats.sent || 0, bg: "bg-emerald-50 border-emerald-200", text: "text-emerald-700" },
                        { label: "Failed", value: followUpStats.failed || 0, bg: "bg-red-50 border-red-200", text: "text-red-700" },
                        { label: "Skipped", value: followUpStats.skipped || 0, bg: "bg-slate-50 border-slate-200", text: "text-slate-500" },
                      ].map(s => (
                        <div key={s.label} className={`border p-4 rounded-lg ${s.bg}`}>
                          <div className={`text-2xl font-extrabold ${s.text}`} style={{ fontFamily: "'JetBrains Mono', monospace" }}>{s.value}</div>
                          <div className="text-xs font-bold uppercase tracking-wide text-slate-500">{s.label}</div>
                        </div>
                      ))}
                    </div>

                    {/* Process Pending Button */}
                    <div className="flex justify-end">
                      <button onClick={processPendingFollowUps} disabled={processingFollowUps || (followUpStats.pending || 0) === 0}
                        className="flex items-center gap-1.5 px-4 py-2 text-xs font-bold uppercase tracking-wider bg-emerald-600 text-white hover:bg-emerald-700 transition-colors rounded-md disabled:opacity-50"
                        data-testid="followups-process-btn">
                        <RefreshCw className={`w-3.5 h-3.5 ${processingFollowUps ? "animate-spin" : ""}`} />
                        {processingFollowUps ? "Processing..." : `Send ${followUpStats.pending || 0} Pending`}
                      </button>
                    </div>

                    {/* Follow-ups Table */}
                    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-slate-50 border-b border-slate-200">
                            <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Lead</th>
                            <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Type</th>
                            <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500 hidden md:table-cell">Channel</th>
                            <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500 hidden lg:table-cell">Scheduled</th>
                            <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Status</th>
                            <th className="text-right px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {followUps.map((fu) => (
                            <tr key={fu.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`followup-row-${fu.id}`}>
                              <td className="px-4 py-3">
                                <div className="font-medium text-slate-900">{fu.lead_name}</div>
                                <div className="text-xs text-slate-400">{fu.lead_email}</div>
                              </td>
                              <td className="px-4 py-3">
                                <span className={`text-xs font-bold px-2 py-0.5 rounded ${
                                  fu.follow_up_type?.includes("hot") ? "bg-red-100 text-red-600"
                                  : fu.follow_up_type?.includes("warm") ? "bg-amber-100 text-amber-600"
                                  : fu.follow_up_type?.includes("whatsapp") ? "bg-emerald-100 text-emerald-600"
                                  : "bg-blue-100 text-blue-600"
                                }`}>
                                  {fu.follow_up_type?.replace(/_/g, " ")}
                                </span>
                              </td>
                              <td className="px-4 py-3 hidden md:table-cell">
                                <span className={`text-xs font-bold uppercase ${fu.channel === "whatsapp" ? "text-emerald-600" : "text-blue-600"}`}>
                                  {fu.channel}
                                </span>
                              </td>
                              <td className="px-4 py-3 hidden lg:table-cell text-xs text-slate-500">
                                {fu.scheduled_at?.slice(0, 16).replace("T", " ")}
                              </td>
                              <td className="px-4 py-3">
                                <span className={`text-xs font-extrabold px-2 py-0.5 rounded-full ${
                                  fu.status === "sent" ? "bg-emerald-100 text-emerald-700"
                                  : fu.status === "pending" ? "bg-amber-100 text-amber-700"
                                  : fu.status === "failed" ? "bg-red-100 text-red-700"
                                  : "bg-slate-100 text-slate-500"
                                }`}>{fu.status}</span>
                              </td>
                              <td className="px-4 py-3 text-right">
                                {fu.status === "pending" && (
                                  <button onClick={() => sendSingleFollowUp(fu.id)}
                                    className="text-xs font-bold text-orange-500 hover:text-orange-600 uppercase tracking-wider"
                                    data-testid={`followup-send-${fu.id}`}>
                                    Send Now
                                  </button>
                                )}
                                {fu.status === "sent" && fu.sent_at && (
                                  <span className="text-xs text-slate-400">{fu.sent_at?.slice(0, 16).replace("T", " ")}</span>
                                )}
                                {fu.status === "failed" && (
                                  <span className="text-xs text-red-400" title={fu.error}>Retry →</span>
                                )}
                              </td>
                            </tr>
                          ))}
                          {followUps.length === 0 && (
                            <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-400">
                              No follow-ups yet. They are auto-created when new bookings or enquiries come in.
                            </td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    {/* Follow-up Schedule Info */}
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-xs text-slate-500">
                      <p className="font-bold text-slate-700 uppercase tracking-wide mb-2">Automated Follow-up Schedule</p>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div className="flex items-start gap-2">
                          <Flame className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                          <div><strong className="text-red-600">Hot Leads</strong><br/>Instant WhatsApp + 2h AI email</div>
                        </div>
                        <div className="flex items-start gap-2">
                          <Zap className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                          <div><strong className="text-amber-600">Warm Leads</strong><br/>24h AI-personalized email</div>
                        </div>
                        <div className="flex items-start gap-2">
                          <Snowflake className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                          <div><strong className="text-blue-600">Cold Leads</strong><br/>7-day newsletter email</div>
                        </div>
                      </div>
                      <p className="mt-2 text-slate-400">Messages are auto-generated by GPT-5-nano AI. Scheduler runs every 30 minutes.</p>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Plots Tab */}
          {activeTab === "plots" && (
            <div data-testid="admin-plots">
              <div className="flex items-center justify-between mb-4">
                <p className="text-slate-500 text-sm">{plots.length} plots total</p>
                <button
                  onClick={() => { setShowPlotForm(true); setEditingPlot(null); setPlotForm(DEFAULT_PLOT_FORM); }}
                  className="flex items-center gap-1.5 bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 text-sm font-bold uppercase tracking-wide transition-colors"
                  data-testid="admin-add-plot-btn"
                >
                  <Plus className="w-4 h-4" /> Add Plot
                </button>
              </div>

              {/* Plot Form */}
              {showPlotForm && (
                <div className="bg-white border border-slate-200 p-5 mb-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold text-slate-900 uppercase text-sm tracking-wide">
                      {editingPlot ? "Edit Plot" : "Add New Plot"}
                    </h3>
                    <button onClick={() => { setShowPlotForm(false); setEditingPlot(null); }}>
                      <X className="w-4 h-4 text-slate-400 hover:text-slate-600" />
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Size (Sq.Mtr)</label>
                      <select
                        value={plotForm.size}
                        onChange={(e) => setPlotForm({ ...plotForm, size: e.target.value })}
                        className="w-full border border-slate-600 px-3 py-2 text-sm focus:outline-none focus:border-orange-500 bg-slate-900 text-white placeholder-slate-400"
                        data-testid="admin-plot-size-select"
                      >
                        {PLOT_SIZES.map((s) => <option key={s} value={s}>{s.toLocaleString("en-IN")} sqmtr</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Price per Sq.Mtr (₹)</label>
                      <input
                        type="number"
                        value={plotForm.price_per_sqmtr}
                        onChange={(e) => setPlotForm({ ...plotForm, price_per_sqmtr: e.target.value })}
                        className="w-full border border-slate-600 px-3 py-2 text-sm focus:outline-none focus:border-orange-500 bg-slate-900 text-white placeholder-slate-400"
                        data-testid="admin-plot-price-input"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Status</label>
                      <select
                        value={plotForm.status}
                        onChange={(e) => setPlotForm({ ...plotForm, status: e.target.value })}
                        className="w-full border border-slate-600 px-3 py-2 text-sm focus:outline-none focus:border-orange-500 bg-slate-900 text-white placeholder-slate-400"
                        data-testid="admin-plot-status-select"
                      >
                        <option value="available">Available</option>
                        <option value="reserved">Reserved</option>
                        <option value="sold">Sold</option>
                      </select>
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-xs font-medium text-slate-600 mb-1">Features (comma-separated)</label>
                      <input
                        type="text"
                        value={plotForm.features}
                        onChange={(e) => setPlotForm({ ...plotForm, features: e.target.value })}
                        placeholder="Corner Plot, Road Facing, SIDA Approved"
                        className="w-full border border-slate-600 px-3 py-2 text-sm focus:outline-none focus:border-orange-500 bg-slate-900 text-white placeholder-slate-400"
                        data-testid="admin-plot-features-input"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Image URL</label>
                      <input
                        type="text"
                        value={plotForm.image_url}
                        onChange={(e) => setPlotForm({ ...plotForm, image_url: e.target.value })}
                        placeholder="https://..."
                        className="w-full border border-slate-600 px-3 py-2 text-sm focus:outline-none focus:border-orange-500 bg-slate-900 text-white placeholder-slate-400"
                        data-testid="admin-plot-image-input"
                      />
                    </div>
                    <div className="md:col-span-3">
                      <label className="block text-xs font-medium text-slate-600 mb-1">Description</label>
                      <textarea
                        value={plotForm.description}
                        onChange={(e) => setPlotForm({ ...plotForm, description: e.target.value })}
                        rows={2}
                        placeholder="Plot description..."
                        className="w-full border border-slate-600 px-3 py-2 text-sm focus:outline-none focus:border-orange-500 bg-slate-900 text-white placeholder-slate-400 resize-none"
                        data-testid="admin-plot-desc-input"
                      />
                    </div>
                  </div>
                  <div className="flex gap-3 mt-4">
                    <button
                      onClick={savePlot}
                      className="flex items-center gap-1.5 bg-slate-900 hover:bg-slate-700 text-white px-5 py-2 text-sm font-bold uppercase tracking-wide transition-colors"
                      data-testid="admin-save-plot-btn"
                    >
                      <Check className="w-4 h-4" /> {editingPlot ? "Update Plot" : "Save Plot"}
                    </button>
                    <button
                      onClick={() => { setShowPlotForm(false); setEditingPlot(null); }}
                      className="px-4 py-2 border border-slate-300 text-slate-600 text-sm hover:border-slate-400 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Plots Table */}
              <div className="bg-white border border-slate-200 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Size</th>
                      <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Price/sqmtr</th>
                      <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Total Value</th>
                      <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Status</th>
                      <th className="text-right px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {plots.map((plot) => (
                      <tr key={plot.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`admin-plot-row-${plot.size}`}>
                        <td className="px-4 py-3 font-bold" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                          {plot.size?.toLocaleString("en-IN")} sqmtr
                        </td>
                        <td className="px-4 py-3 text-orange-600 font-semibold" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                          ₹{plot.price_per_sqmtr?.toLocaleString("en-IN")}
                        </td>
                        <td className="px-4 py-3 text-slate-600">
                          {plot.total_price >= 10000000
                            ? `₹${(plot.total_price / 10000000).toFixed(2)} Cr`
                            : `₹${(plot.total_price / 100000).toFixed(2)} L`}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 text-xs font-bold rounded-sm ${STATUS_COLORS[plot.status] || "bg-slate-100 text-slate-600"}`}>
                            {plot.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => startEditPlot(plot)}
                              className="text-slate-400 hover:text-blue-600 transition-colors"
                              data-testid={`admin-edit-plot-${plot.size}`}
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => deletePlot(plot.id)}
                              className="text-slate-400 hover:text-red-600 transition-colors"
                              data-testid={`admin-delete-plot-${plot.size}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Gallery Tab */}
          {activeTab === "gallery" && (
            <div data-testid="admin-gallery">
              <div className="flex items-center justify-between mb-4">
                <p className="text-slate-500 text-sm">{gallery.length} images</p>
                <button
                  onClick={() => setShowGalleryForm(true)}
                  className="flex items-center gap-1.5 bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 text-sm font-bold uppercase tracking-wide transition-colors"
                  data-testid="admin-add-gallery-btn"
                >
                  <Plus className="w-4 h-4" /> Add Image
                </button>
              </div>

              {showGalleryForm && (
                <div className="bg-white border border-slate-200 p-5 mb-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold text-slate-900 text-sm uppercase tracking-wide">Add Gallery Image</h3>
                    <button onClick={() => setShowGalleryForm(false)}>
                      <X className="w-4 h-4 text-slate-400 hover:text-slate-600" />
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Title</label>
                      <input
                        type="text"
                        value={galleryForm.title}
                        onChange={(e) => setGalleryForm({ ...galleryForm, title: e.target.value })}
                        placeholder="e.g. Manufacturing Unit"
                        className="w-full border border-slate-600 px-3 py-2 text-sm focus:outline-none focus:border-orange-500 bg-slate-900 text-white placeholder-slate-400"
                        data-testid="admin-gallery-title-input"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Category</label>
                      <select
                        value={galleryForm.category}
                        onChange={(e) => setGalleryForm({ ...galleryForm, category: e.target.value })}
                        className="w-full border border-slate-600 px-3 py-2 text-sm focus:outline-none focus:border-orange-500 bg-slate-900 text-white placeholder-slate-400"
                        data-testid="admin-gallery-category-select"
                      >
                        <option value="industry">Industry</option>
                        <option value="plots">Plots</option>
                        <option value="aerial">Aerial</option>
                      </select>
                    </div>
                  </div>
                  <div className="mt-4">
                    <label className="block text-xs font-medium text-slate-600 mb-2">Image (Upload or paste URL)</label>
                    <div className="flex flex-col md:flex-row gap-3">
                      <label className={`flex items-center gap-2 px-4 py-2.5 border-2 border-dashed border-slate-300 hover:border-orange-400 cursor-pointer transition-colors text-sm ${uploadingImage ? 'opacity-50 pointer-events-none' : ''}`}>
                        <Upload className="w-4 h-4 text-orange-500" />
                        <span className="text-slate-600">{uploadingImage ? "Uploading..." : "Upload Image"}</span>
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/gif,image/webp"
                          onChange={handleImageUpload}
                          className="hidden"
                          data-testid="admin-gallery-file-input"
                        />
                      </label>
                      <span className="text-xs text-slate-400 self-center">OR</span>
                      <input
                        type="text"
                        value={galleryForm.image_url}
                        onChange={(e) => setGalleryForm({ ...galleryForm, image_url: e.target.value })}
                        placeholder="Paste image URL here..."
                        className="flex-1 border border-slate-600 px-3 py-2 text-sm focus:outline-none focus:border-orange-500 bg-slate-900 text-white placeholder-slate-400"
                        data-testid="admin-gallery-url-input"
                      />
                    </div>
                    {galleryForm.image_url && (
                      <div className="mt-2 flex items-center gap-2">
                        <img src={galleryForm.image_url} alt="Preview" className="w-16 h-16 object-cover border border-slate-200" 
                          onError={(e) => e.target.style.display = 'none'} />
                        <span className="text-xs text-emerald-600 font-medium">Image ready</span>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-3 mt-4">
                    <button
                      onClick={saveGallery}
                      className="flex items-center gap-1.5 bg-slate-900 hover:bg-slate-700 text-white px-5 py-2 text-sm font-bold uppercase tracking-wide transition-colors"
                      data-testid="admin-save-gallery-btn"
                    >
                      <Check className="w-4 h-4" /> Save Image
                    </button>
                    <button
                      onClick={() => setShowGalleryForm(false)}
                      className="px-4 py-2 border border-slate-300 text-slate-600 text-sm hover:border-slate-400 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {gallery.map((item) => (
                  <div key={item.id} className="relative group bg-white border border-slate-200 overflow-hidden" data-testid={`admin-gallery-item-${item.id}`}>
                    <img
                      src={item.image_url}
                      alt={item.title}
                      className="w-full h-32 object-cover"
                      onError={(e) => {
                        e.target.src = "https://images.unsplash.com/photo-1668301952339-53ba65165d2e?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85";
                      }}
                    />
                    <button
                      onClick={() => deleteGallery(item.id)}
                      className="absolute top-2 right-2 bg-red-500 text-white p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`admin-delete-gallery-${item.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                    <div className="p-2 border-t border-slate-100">
                      <p className="text-xs font-medium text-slate-700 truncate">{item.title}</p>
                      <p className="text-xs text-slate-400 capitalize">{item.category}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Enquiries Tab */}
          {activeTab === "enquiries" && (
            <div data-testid="admin-enquiries">
              <div className="flex items-center justify-between mb-4">
                <p className="text-slate-500 text-sm">{enquiries.length} total | {enquiries.filter((e) => e.status === "new").length} new</p>
              </div>
              <div className="bg-white border border-slate-200 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Name</th>
                      <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500 hidden md:table-cell">Contact</th>
                      <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500 hidden lg:table-cell">Plot Size</th>
                      <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500 hidden xl:table-cell">Message</th>
                      <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Status</th>
                      <th className="text-right px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {enquiries.map((enq) => (
                      <tr key={enq.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`admin-enquiry-row-${enq.id}`}>
                        <td className="px-4 py-3">
                          <div className="font-medium text-slate-900">{enq.name}</div>
                          <div className="text-xs text-slate-400 md:hidden">{enq.phone}</div>
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell">
                          <div className="text-slate-700">{enq.phone}</div>
                          <div className="text-xs text-slate-400">{enq.email}</div>
                        </td>
                        <td className="px-4 py-3 hidden lg:table-cell text-slate-600">{enq.plot_size || "—"}</td>
                        <td className="px-4 py-3 hidden xl:table-cell">
                          <p className="text-slate-600 truncate max-w-xs">{enq.message}</p>
                        </td>
                        <td className="px-4 py-3">
                          <select
                            value={enq.status}
                            onChange={(e) => updateEnquiryStatus(enq.id, e.target.value)}
                            className={`text-xs font-bold px-2 py-1 border-0 outline-none cursor-pointer ${ENQ_COLORS[enq.status]}`}
                            data-testid={`admin-enquiry-status-${enq.id}`}
                          >
                            <option value="new">New</option>
                            <option value="contacted">Contacted</option>
                            <option value="closed">Closed</option>
                          </select>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => deleteEnquiry(enq.id)}
                            className="text-slate-400 hover:text-red-600 transition-colors"
                            data-testid={`admin-delete-enquiry-${enq.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                    {enquiries.length === 0 && (
                      <tr>
                        <td colSpan={6} className="px-4 py-10 text-center text-slate-400 text-sm">
                          No enquiries yet
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {/* Bookings Tab */}
          {activeTab === "bookings" && (
            <div data-testid="admin-bookings">
              <div className="flex items-center justify-between mb-4">
                <p className="text-slate-500 text-sm">{bookings.length} total | {bookings.filter((b) => b.status === "new").length} new</p>
              </div>
              <div className="bg-white border border-slate-200 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Applicant</th>
                      <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500 hidden md:table-cell">Contact</th>
                      <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Plot Size</th>
                      <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500 hidden lg:table-cell">Industry</th>
                      <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500 hidden xl:table-cell">Payment</th>
                      <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Status</th>
                      <th className="text-right px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bookings.map((booking) => (
                      <tr key={booking.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`admin-booking-row-${booking.id}`}>
                        <td className="px-4 py-3">
                          <div className="font-medium text-slate-900">{booking.full_name}</div>
                          {booking.company_name && <div className="text-xs text-slate-400">{booking.company_name}</div>}
                          <div className="text-xs text-slate-400 md:hidden">{booking.phone}</div>
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell">
                          <div className="text-slate-700 text-sm">{booking.phone}</div>
                          <div className="text-xs text-slate-400">{booking.email}</div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-orange-600 font-semibold text-xs" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                            {booking.plot_size?.replace(/\s*\(.*\)/, "")}
                          </span>
                        </td>
                        <td className="px-4 py-3 hidden lg:table-cell text-slate-600 text-xs">{booking.industry_type}</td>
                        <td className="px-4 py-3 hidden xl:table-cell text-slate-600 text-xs">{booking.payment_mode}</td>
                        <td className="px-4 py-3">
                          <select
                            value={booking.status}
                            onChange={(e) => updateBookingStatus(booking.id, e.target.value)}
                            className={`text-xs font-bold px-2 py-1 border-0 outline-none cursor-pointer rounded-sm ${
                              booking.status === "new" ? "bg-blue-100 text-blue-700"
                                : booking.status === "confirmed" ? "bg-emerald-100 text-emerald-700"
                                : booking.status === "site_visit" ? "bg-purple-100 text-purple-700"
                                : "bg-slate-100 text-slate-500"
                            }`}
                            data-testid={`admin-booking-status-${booking.id}`}
                          >
                            <option value="new">New</option>
                            <option value="contacted">Contacted</option>
                            <option value="site_visit">Site Visit</option>
                            <option value="confirmed">Confirmed</option>
                            <option value="cancelled">Cancelled</option>
                          </select>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button onClick={() => setSelectedBooking(booking)} className="text-slate-400 hover:text-blue-600 transition-colors text-xs font-medium" data-testid={`admin-view-booking-${booking.id}`}>View</button>
                            <button onClick={() => deleteBooking(booking.id)} className="text-slate-400 hover:text-red-600 transition-colors" data-testid={`admin-delete-booking-${booking.id}`}><Trash2 className="w-4 h-4" /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {bookings.length === 0 && (
                      <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400 text-sm">No booking requests yet</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Reviews Tab */}
          {activeTab === "reviews" && (
            <div data-testid="admin-reviews">
              <div className="flex items-center justify-between mb-4">
                <p className="text-slate-500 text-sm">
                  {reviews.length} total | {reviews.filter((r) => r.status === "pending").length} pending | {reviews.filter((r) => r.status === "approved").length} published
                </p>
              </div>
              <div className="space-y-3">
                {reviews.map((r) => (
                  <div key={r.id} className="bg-white border border-slate-200 p-4" data-testid={`admin-review-${r.id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="flex-1 min-w-[240px]">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-bold text-slate-900">{r.author}</span>
                          <span className="flex items-center gap-0.5">
                            {[1, 2, 3, 4, 5].map((n) => (
                              <Star key={n} className={`w-3.5 h-3.5 ${n <= r.rating ? "text-orange-500 fill-orange-500" : "text-slate-300"}`} />
                            ))}
                          </span>
                          <span className={`text-xs font-bold px-2 py-0.5 rounded-sm ${
                            r.status === "approved" ? "bg-emerald-100 text-emerald-700"
                              : r.status === "rejected" ? "bg-red-100 text-red-600"
                              : "bg-amber-100 text-amber-700"
                          }`}>{r.status}</span>
                        </div>
                        <div className="text-xs text-slate-400 mb-2">
                          {[r.role, r.location].filter(Boolean).join(" · ") || "—"}
                        </div>
                        <p className="text-slate-600 text-sm">"{r.text}"</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {r.status !== "approved" && (
                          <button
                            onClick={() => updateReviewStatus(r.id, "approved")}
                            className="flex items-center gap-1 bg-emerald-500 hover:bg-emerald-600 text-white px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors"
                            data-testid={`admin-approve-review-${r.id}`}
                          >
                            <Check className="w-3.5 h-3.5" /> Approve
                          </button>
                        )}
                        {r.status === "approved" && (
                          <button
                            onClick={() => updateReviewStatus(r.id, "pending")}
                            className="flex items-center gap-1 border border-slate-300 text-slate-600 hover:border-amber-500 hover:text-amber-600 px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors"
                            data-testid={`admin-unpublish-review-${r.id}`}
                          >
                            <EyeOff className="w-3.5 h-3.5" /> Unpublish
                          </button>
                        )}
                        <button
                          onClick={() => deleteReview(r.id)}
                          className="text-slate-400 hover:text-red-600 transition-colors"
                          data-testid={`admin-delete-review-${r.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                {reviews.length === 0 && (
                  <div className="bg-white border border-slate-200 px-4 py-10 text-center text-slate-400 text-sm">
                    No reviews yet
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Site Plots Tab */}
          {activeTab === "site-plots" && (
            <div data-testid="admin-site-plots">
              <div className="flex items-center justify-between mb-4">
                <p className="text-slate-500 text-sm">
                  {sitePlots.length} plots |
                  <span className="text-emerald-600 ml-1">{sitePlots.filter(p => p.status === "available").length} available</span> |
                  <span className="text-amber-600 ml-1">{sitePlots.filter(p => p.status === "reserved").length} reserved</span> |
                  <span className="text-slate-600 ml-1">{sitePlots.filter(p => p.status === "sold").length} sold</span>
                </p>
              </div>
              <div className="bg-white border border-slate-200 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Plot ID</th>
                      <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Size (sqmt)</th>
                      <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500 hidden md:table-cell">Type</th>
                      <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500 hidden lg:table-cell">Price/sqmt</th>
                      <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500 hidden lg:table-cell">Total</th>
                      <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500 hidden md:table-cell">Facing</th>
                      <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sitePlots.map((plot) => (
                      <tr key={plot.plot_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`admin-site-plot-${plot.plot_id}`}>
                        <td className="px-4 py-2.5 font-bold text-slate-900" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{plot.plot_id}</td>
                        <td className="px-4 py-2.5" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{plot.plot_size_sqmt?.toLocaleString()}</td>
                        <td className="px-4 py-2.5 hidden md:table-cell">
                          <span className={`text-xs font-bold px-2 py-0.5 ${plot.plot_type === "Industrial" ? "bg-blue-50 text-blue-600" : plot.plot_type === "Commercial" ? "bg-orange-50 text-orange-600" : "bg-purple-50 text-purple-600"}`}>
                            {plot.plot_type}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 hidden lg:table-cell text-orange-600" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                          {`\u20B9${plot.price_per_sqmt?.toLocaleString()}`}
                        </td>
                        <td className="px-4 py-2.5 hidden lg:table-cell text-slate-600 text-xs">
                          {plot.total_price >= 10000000 ? `\u20B9${(plot.total_price / 10000000).toFixed(2)} Cr` : `\u20B9${(plot.total_price / 100000).toFixed(2)} L`}
                        </td>
                        <td className="px-4 py-2.5 hidden md:table-cell text-slate-500 text-xs">{plot.facing}{plot.corner_plot ? " (Corner)" : ""}</td>
                        <td className="px-4 py-2.5">
                          <select
                            value={plot.status}
                            onChange={(e) => updateSitePlotStatus(plot.plot_id, e.target.value)}
                            className={`text-xs font-bold px-2 py-1 border-0 outline-none cursor-pointer ${STATUS_COLORS[plot.status] || "bg-slate-100 text-slate-600"}`}
                            data-testid={`admin-site-plot-status-${plot.plot_id}`}
                          >
                            <option value="available">Available</option>
                            <option value="reserved">Reserved</option>
                            <option value="sold">Sold</option>
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Blog Tab */}
          {activeTab === "blog" && (
            <div data-testid="admin-blog">
              <div className="flex items-center justify-between mb-4">
                <p className="text-slate-500 text-sm">{blogPosts.length} posts</p>
                <div className="flex gap-2">
                  <button
                    onClick={autoGenerateBlog}
                    disabled={generatingBlog}
                    className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 text-sm font-bold uppercase tracking-wide transition-colors disabled:opacity-50"
                    data-testid="admin-auto-generate-blog-btn"
                  >
                    <Wand2 className="w-4 h-4" /> {generatingBlog ? "Generating..." : "AI Auto-Post"}
                  </button>
                  <button
                    onClick={() => { setShowBlogForm(true); setEditingBlog(null); setBlogForm({ title: "", content: "", excerpt: "", image_url: "", category: "news", published: true }); }}
                    className="flex items-center gap-1.5 bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 text-sm font-bold uppercase tracking-wide transition-colors"
                    data-testid="admin-add-blog-btn"
                  >
                    <Plus className="w-4 h-4" /> New Post
                  </button>
                </div>
              </div>

              {showBlogForm && (
                <div className="bg-white border border-slate-200 p-5 mb-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold text-slate-900 uppercase text-sm tracking-wide">{editingBlog ? "Edit Post" : "New Blog Post"}</h3>
                    <button onClick={() => { setShowBlogForm(false); setEditingBlog(null); }}><X className="w-4 h-4 text-slate-400 hover:text-slate-600" /></button>
                  </div>
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Title</label>
                        <input value={blogForm.title} onChange={(e) => setBlogForm({ ...blogForm, title: e.target.value })} placeholder="Post title"
                          className="w-full border border-slate-600 px-3 py-2 text-sm focus:outline-none focus:border-orange-500 bg-slate-900 text-white placeholder-slate-400" data-testid="admin-blog-title-input" />
                      </div>
                      <div className="flex gap-4">
                        <div className="flex-1">
                          <label className="block text-xs font-medium text-slate-600 mb-1">Category</label>
                          <select value={blogForm.category} onChange={(e) => setBlogForm({ ...blogForm, category: e.target.value })}
                            className="w-full border border-slate-600 px-3 py-2 text-sm focus:outline-none focus:border-orange-500 bg-slate-900 text-white placeholder-slate-400" data-testid="admin-blog-category-select">
                            <option value="news">News</option>
                            <option value="updates">Updates</option>
                            <option value="guides">Guides</option>
                            <option value="policy">Policy</option>
                          </select>
                        </div>
                        <div className="flex items-end">
                          <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer pb-2">
                            <input type="checkbox" checked={blogForm.published} onChange={(e) => setBlogForm({ ...blogForm, published: e.target.checked })} className="text-orange-500" />
                            Published
                          </label>
                        </div>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Excerpt (short summary)</label>
                      <input value={blogForm.excerpt} onChange={(e) => setBlogForm({ ...blogForm, excerpt: e.target.value })} placeholder="Short description..."
                        className="w-full border border-slate-600 px-3 py-2 text-sm focus:outline-none focus:border-orange-500 bg-slate-900 text-white placeholder-slate-400" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Image URL</label>
                      <input value={blogForm.image_url} onChange={(e) => setBlogForm({ ...blogForm, image_url: e.target.value })} placeholder="https://..."
                        className="w-full border border-slate-600 px-3 py-2 text-sm focus:outline-none focus:border-orange-500 bg-slate-900 text-white placeholder-slate-400" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Content</label>
                      <textarea value={blogForm.content} onChange={(e) => setBlogForm({ ...blogForm, content: e.target.value })} rows={8} placeholder="Write your post content..."
                        className="w-full border border-slate-600 px-3 py-2 text-sm focus:outline-none focus:border-orange-500 bg-slate-900 text-white placeholder-slate-400 resize-none" data-testid="admin-blog-content-input" />
                    </div>
                    <div className="flex gap-3">
                      <button onClick={saveBlog} className="flex items-center gap-1.5 bg-slate-900 hover:bg-slate-700 text-white px-5 py-2 text-sm font-bold uppercase tracking-wide transition-colors" data-testid="admin-save-blog-btn">
                        <Check className="w-4 h-4" /> {editingBlog ? "Update" : "Publish"}
                      </button>
                      <button onClick={() => { setShowBlogForm(false); setEditingBlog(null); }} className="px-4 py-2 border border-slate-300 text-slate-600 text-sm hover:border-slate-400 transition-colors">Cancel</button>
                    </div>
                  </div>
                </div>
              )}

              <div className="bg-white border border-slate-200 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Title</th>
                      <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500 hidden md:table-cell">Category</th>
                      <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500 hidden lg:table-cell">Date</th>
                      <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Status</th>
                      <th className="text-right px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {blogPosts.map((post) => (
                      <tr key={post.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`admin-blog-row-${post.id}`}>
                        <td className="px-4 py-3 font-medium text-slate-900 max-w-xs truncate">{post.title}</td>
                        <td className="px-4 py-3 hidden md:table-cell"><span className="text-xs font-bold px-2 py-0.5 bg-slate-100 text-slate-600 capitalize">{post.category}</span></td>
                        <td className="px-4 py-3 hidden lg:table-cell text-slate-500 text-xs">{post.created_at?.slice(0, 10)}</td>
                        <td className="px-4 py-3">
                          <span className={`text-xs font-bold px-2 py-0.5 ${post.published ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                            {post.published ? "Published" : "Draft"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button onClick={() => { setEditingBlog(post); setBlogForm({ title: post.title, content: post.content, excerpt: post.excerpt || "", image_url: post.image_url || "", category: post.category, published: post.published }); setShowBlogForm(true); }} className="text-slate-400 hover:text-blue-600 transition-colors"><Edit2 className="w-4 h-4" /></button>
                            <button onClick={() => deleteBlog(post.id)} className="text-slate-400 hover:text-red-600 transition-colors"><Trash2 className="w-4 h-4" /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {blogPosts.length === 0 && (<tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400 text-sm">No blog posts yet</td></tr>)}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* SEO Tab */}
          {activeTab === "seo" && (
            <div data-testid="admin-seo">
              <div className="flex items-center justify-between mb-4">
                <p className="text-slate-500 text-sm">Manage SEO meta tags for each page</p>
                <button
                  onClick={() => { setShowSeoForm(true); setSeoForm({ page: "home", title: "", description: "", keywords: "", og_image: "" }); }}
                  className="flex items-center gap-1.5 bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 text-sm font-bold uppercase tracking-wide transition-colors"
                  data-testid="admin-add-seo-btn"
                >
                  <Plus className="w-4 h-4" /> Add / Edit SEO
                </button>
              </div>

              {showSeoForm && (
                <div className="bg-white border border-slate-200 p-5 mb-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold text-slate-900 uppercase text-sm tracking-wide">Edit Page SEO</h3>
                    <button onClick={() => setShowSeoForm(false)}><X className="w-4 h-4 text-slate-400 hover:text-slate-600" /></button>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Page</label>
                      <select value={seoForm.page} onChange={(e) => {
                        const existing = seoList.find(s => s.page === e.target.value);
                        setSeoForm(existing ? { ...existing } : { page: e.target.value, title: "", description: "", keywords: "", og_image: "" });
                      }} className="w-full border border-slate-600 px-3 py-2 text-sm focus:outline-none focus:border-orange-500 bg-slate-900 text-white placeholder-slate-400" data-testid="admin-seo-page-select">
                        {["home","plots","gallery","schemes","finance","booking","contact","calculator","blog"].map(p => <option key={p} value={p}>{p}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Title</label>
                      <input value={seoForm.title} onChange={(e) => setSeoForm({ ...seoForm, title: e.target.value })} placeholder="Page title for search engines"
                        className="w-full border border-slate-600 px-3 py-2 text-sm focus:outline-none focus:border-orange-500 bg-slate-900 text-white placeholder-slate-400" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Description</label>
                      <textarea value={seoForm.description} onChange={(e) => setSeoForm({ ...seoForm, description: e.target.value })} rows={2} placeholder="Meta description (150-160 chars)"
                        className="w-full border border-slate-600 px-3 py-2 text-sm focus:outline-none focus:border-orange-500 bg-slate-900 text-white placeholder-slate-400 resize-none" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Keywords</label>
                      <input value={seoForm.keywords} onChange={(e) => setSeoForm({ ...seoForm, keywords: e.target.value })} placeholder="keyword1, keyword2, keyword3"
                        className="w-full border border-slate-600 px-3 py-2 text-sm focus:outline-none focus:border-orange-500 bg-slate-900 text-white placeholder-slate-400" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">OG Image URL</label>
                      <input value={seoForm.og_image} onChange={(e) => setSeoForm({ ...seoForm, og_image: e.target.value })} placeholder="https://..."
                        className="w-full border border-slate-600 px-3 py-2 text-sm focus:outline-none focus:border-orange-500 bg-slate-900 text-white placeholder-slate-400" />
                    </div>
                    <button onClick={saveSeo} className="flex items-center gap-1.5 bg-slate-900 hover:bg-slate-700 text-white px-5 py-2 text-sm font-bold uppercase tracking-wide transition-colors" data-testid="admin-save-seo-btn">
                      <Save className="w-4 h-4" /> Save SEO
                    </button>
                  </div>
                </div>
              )}

              <div className="bg-white border border-slate-200 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Page</th>
                      <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Title</th>
                      <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500 hidden lg:table-cell">Description</th>
                      <th className="text-right px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {seoList.map((seo) => (
                      <tr key={seo.page} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="px-4 py-3 font-bold text-slate-900 capitalize">{seo.page}</td>
                        <td className="px-4 py-3 text-slate-600 max-w-xs truncate">{seo.title || "—"}</td>
                        <td className="px-4 py-3 hidden lg:table-cell text-slate-500 text-xs max-w-sm truncate">{seo.description || "—"}</td>
                        <td className="px-4 py-3 text-right">
                          <button onClick={() => { setSeoForm({ ...seo }); setShowSeoForm(true); }} className="text-slate-400 hover:text-blue-600 transition-colors">
                            <Edit2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                    {seoList.length === 0 && (<tr><td colSpan={4} className="px-4 py-10 text-center text-slate-400 text-sm">No custom SEO settings yet. Using defaults.</td></tr>)}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Settings Tab */}
          {activeTab === "payments" && (
            <div className="space-y-6" data-testid="admin-payments">
              {/* Payment Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard label="Total Payments" value={paymentStats.total || 0} color="text-slate-900" />
                <StatCard label="Paid" value={paymentStats.paid || 0} color="text-emerald-600" />
                <StatCard label="Pending" value={paymentStats.pending || 0} color="text-amber-600" />
                <StatCard label="Total Collected" value={paymentStats.total_collected_paise ? `₹${(paymentStats.total_collected_paise / 100).toLocaleString("en-IN")}` : "₹0"} color="text-orange-600" />
              </div>

              {/* Payments Table */}
              <div className="bg-white border border-slate-200 overflow-hidden">
                <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
                  <h3 className="font-bold text-slate-900 text-sm uppercase tracking-wide flex items-center gap-2">
                    <IndianRupee className="w-4 h-4" /> Payment History
                  </h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                      <tr>
                        <th className="px-4 py-3 text-left">Customer</th>
                        <th className="px-4 py-3 text-left hidden md:table-cell">Plot Size</th>
                        <th className="px-4 py-3 text-left">Amount</th>
                        <th className="px-4 py-3 text-left">Status</th>
                        <th className="px-4 py-3 text-left hidden lg:table-cell">Date</th>
                        <th className="px-4 py-3 text-left hidden lg:table-cell">Razorpay ID</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {payments.length === 0 ? (
                        <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">No payments yet</td></tr>
                      ) : payments.map((p) => (
                        <tr key={p.id} className="hover:bg-slate-50">
                          <td className="px-4 py-3">
                            <div className="font-medium text-slate-900">{p.customer_name}</div>
                            <div className="text-xs text-slate-400">{p.customer_email}</div>
                          </td>
                          <td className="px-4 py-3 hidden md:table-cell text-slate-600 text-xs">{p.plot_size}</td>
                          <td className="px-4 py-3 font-bold text-orange-600" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                            ₹{(p.amount / 100).toLocaleString("en-IN")}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`text-xs font-bold px-2 py-0.5 ${
                              p.status === "paid" ? "bg-emerald-100 text-emerald-700" :
                              p.status === "created" ? "bg-amber-100 text-amber-700" :
                              "bg-red-100 text-red-700"
                            }`}>{p.status}</span>
                          </td>
                          <td className="px-4 py-3 hidden lg:table-cell text-slate-400 text-xs">{p.created_at?.slice(0, 10)}</td>
                          <td className="px-4 py-3 hidden lg:table-cell text-slate-400 text-xs font-mono">{p.razorpay_payment_id || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {!paymentStats.total && (
                <div className="bg-amber-50 border border-amber-200 p-4 text-sm text-amber-800">
                  <strong>Note:</strong> Razorpay payment gateway requires API keys to be configured in the backend environment. Contact your developer to set up RAZORPAY_KEY_ID and RAZORPAY_KEY_SECRET.
                </div>
              )}
            </div>
          )}

          {activeTab === "virtual-tour" && (
            <div className="space-y-6" data-testid="admin-virtual-tour">
              <div className="flex items-center justify-between">
                <h3 className="font-bold text-slate-900 text-sm uppercase tracking-wide flex items-center gap-2">
                  <Camera className="w-4 h-4" /> 360° Tour Scenes ({tourScenes.length})
                </h3>
                <button
                  onClick={() => { setShowTourForm(true); setEditingTour(null); setTourForm({ title: "", description: "", image_url: "", order: tourScenes.length + 1 }); }}
                  className="flex items-center gap-1.5 bg-slate-900 hover:bg-slate-700 text-white px-4 py-2 text-sm font-bold uppercase tracking-wide transition-colors"
                  data-testid="admin-add-tour-btn"
                >
                  <Plus className="w-4 h-4" /> Add Scene
                </button>
              </div>

              {/* Tour Form */}
              {showTourForm && (
                <div className="bg-white border border-slate-200 p-5">
                  <h4 className="font-bold text-slate-900 mb-4 text-sm uppercase">{editingTour ? "Edit Scene" : "New Scene"}</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Title *</label>
                      <input value={tourForm.title} onChange={(e) => setTourForm({ ...tourForm, title: e.target.value })} placeholder="Scene title"
                        className="w-full border border-slate-600 px-3 py-2 text-sm focus:outline-none focus:border-orange-500 bg-slate-900 text-white placeholder-slate-400" data-testid="tour-form-title" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Order</label>
                      <input type="number" value={tourForm.order} onChange={(e) => setTourForm({ ...tourForm, order: e.target.value })}
                        className="w-full border border-slate-600 px-3 py-2 text-sm focus:outline-none focus:border-orange-500 bg-slate-900 text-white placeholder-slate-400" data-testid="tour-form-order" />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-xs font-medium text-slate-600 mb-1">360° Image URL *</label>
                      <input value={tourForm.image_url} onChange={(e) => setTourForm({ ...tourForm, image_url: e.target.value })} placeholder="https://example.com/panorama.jpg"
                        className="w-full border border-slate-600 px-3 py-2 text-sm focus:outline-none focus:border-orange-500 bg-slate-900 text-white placeholder-slate-400" data-testid="tour-form-image" />
                      <p className="text-xs text-slate-400 mt-1">Use equirectangular panoramic images (2:1 aspect ratio)</p>
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-xs font-medium text-slate-600 mb-1">Description</label>
                      <textarea value={tourForm.description} onChange={(e) => setTourForm({ ...tourForm, description: e.target.value })} rows={2} placeholder="Describe this viewpoint..."
                        className="w-full border border-slate-600 px-3 py-2 text-sm focus:outline-none focus:border-orange-500 bg-slate-900 text-white placeholder-slate-400 resize-none" data-testid="tour-form-desc" />
                    </div>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <button onClick={saveTourScene} disabled={!tourForm.title || !tourForm.image_url}
                      className="flex items-center gap-1.5 bg-slate-900 hover:bg-slate-700 text-white px-5 py-2 text-sm font-bold uppercase tracking-wide transition-colors disabled:opacity-50"
                      data-testid="tour-form-save">
                      <Check className="w-4 h-4" /> {editingTour ? "Update" : "Add"} Scene
                    </button>
                    <button onClick={() => { setShowTourForm(false); setEditingTour(null); }}
                      className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900 transition-colors">Cancel</button>
                  </div>
                </div>
              )}

              {/* Tour Scenes Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {tourScenes.map((scene) => (
                  <div key={scene.id} className="bg-white border border-slate-200 overflow-hidden" data-testid={`tour-scene-${scene.id}`}>
                    <div className="h-32 bg-slate-100 relative overflow-hidden">
                      <img src={scene.image_url} alt={scene.title} className="w-full h-full object-cover"
                        onError={(e) => { e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 400 200'%3E%3Crect fill='%23F1F5F9' width='400' height='200'/%3E%3Ctext x='50%25' y='50%25' text-anchor='middle' dy='.3em' fill='%2394A3B8' font-size='14'%3E360° Image%3C/text%3E%3C/svg%3E"; }} />
                      <div className="absolute top-2 left-2 bg-black/60 text-white text-xs font-bold px-2 py-0.5" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                        #{scene.order}
                      </div>
                    </div>
                    <div className="p-3">
                      <h4 className="font-bold text-slate-900 text-sm">{scene.title}</h4>
                      {scene.description && <p className="text-xs text-slate-500 mt-1 line-clamp-2">{scene.description}</p>}
                      <div className="flex gap-2 mt-3">
                        <button
                          onClick={() => { setEditingTour(scene); setTourForm({ title: scene.title, description: scene.description || "", image_url: scene.image_url, order: scene.order }); setShowTourForm(true); }}
                          className="flex items-center gap-1 text-xs text-orange-600 hover:text-orange-700 font-bold"
                          data-testid={`tour-edit-${scene.id}`}
                        >
                          <Edit2 className="w-3 h-3" /> Edit
                        </button>
                        <button
                          onClick={() => deleteTourScene(scene.id)}
                          className="flex items-center gap-1 text-xs text-red-500 hover:text-red-600 font-bold"
                          data-testid={`tour-delete-${scene.id}`}
                        >
                          <Trash2 className="w-3 h-3" /> Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {tourScenes.length === 0 && !showTourForm && (
                <div className="bg-white border border-slate-200 p-8 text-center">
                  <Camera className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500 text-sm">No tour scenes yet. Add your first 360° panoramic scene.</p>
                </div>
              )}
            </div>
          )}

          {activeTab === "settings" && (
            <div className="space-y-6" data-testid="admin-settings">
              {/* Social Media Links */}
              <div className="bg-white border border-slate-200 p-5">
                <h3 className="font-bold text-slate-900 mb-4 text-sm uppercase tracking-wide flex items-center gap-2"><Globe className="w-4 h-4" /> Social Media Links</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    { key: "facebook", label: "Facebook", placeholder: "https://facebook.com/..." },
                    { key: "instagram", label: "Instagram", placeholder: "https://instagram.com/..." },
                    { key: "twitter", label: "Twitter / X", placeholder: "https://twitter.com/..." },
                    { key: "linkedin", label: "LinkedIn", placeholder: "https://linkedin.com/..." },
                    { key: "youtube", label: "YouTube", placeholder: "https://youtube.com/..." },
                  ].map(({ key, label, placeholder }) => (
                    <div key={key}>
                      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
                      <input value={socialLinks[key] || ""} onChange={(e) => setSocialLinks({ ...socialLinks, [key]: e.target.value })} placeholder={placeholder}
                        className="w-full border border-slate-600 px-3 py-2 text-sm focus:outline-none focus:border-orange-500 bg-slate-900 text-white placeholder-slate-400" data-testid={`admin-social-${key}`} />
                    </div>
                  ))}
                </div>
                <button onClick={saveSocialLinks} className="mt-4 flex items-center gap-1.5 bg-slate-900 hover:bg-slate-700 text-white px-5 py-2 text-sm font-bold uppercase tracking-wide transition-colors" data-testid="admin-save-social-btn">
                  <Save className="w-4 h-4" /> Save Social Links
                </button>
              </div>

              {/* Google Analytics */}
              <div className="bg-white border border-slate-200 p-5">
                <h3 className="font-bold text-slate-900 mb-4 text-sm uppercase tracking-wide flex items-center gap-2"><BarChart3 className="w-4 h-4" /> Google Analytics</h3>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">GA4 Measurement ID</label>
                  <input value={analyticsForm.ga_measurement_id || ""} onChange={(e) => setAnalyticsForm({ ...analyticsForm, ga_measurement_id: e.target.value })} placeholder="G-XXXXXXXXXX"
                    className="w-full max-w-md border border-slate-600 px-3 py-2 text-sm focus:outline-none focus:border-orange-500 bg-slate-900 text-white placeholder-slate-400" style={{ fontFamily: "'JetBrains Mono', monospace" }} data-testid="admin-ga-id-input" />
                  <p className="text-xs text-slate-400 mt-1">Enter your Google Analytics 4 Measurement ID. Get it from <a href="https://analytics.google.com" target="_blank" rel="noopener noreferrer" className="text-orange-500 hover:underline">Google Analytics</a></p>
                </div>
                <button onClick={saveAnalytics} className="mt-4 flex items-center gap-1.5 bg-slate-900 hover:bg-slate-700 text-white px-5 py-2 text-sm font-bold uppercase tracking-wide transition-colors" data-testid="admin-save-analytics-btn">
                  <Save className="w-4 h-4" /> Save Analytics
                </button>
              </div>

              {/* Admin Credentials Management */}
              <div className="bg-white border border-slate-200 p-5">
                <h3 className="font-bold text-slate-900 mb-4 text-sm uppercase tracking-wide flex items-center gap-2"><Lock className="w-4 h-4" /> Admin Credentials</h3>
                <p className="text-xs text-slate-500 mb-4">Current username: <span className="font-mono font-bold text-slate-800">{adminUsername || "admin"}</span></p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Current Password *</label>
                    <input type="password" value={credForm.current_password} onChange={(e) => setCredForm({ ...credForm, current_password: e.target.value })}
                      placeholder="Enter current password"
                      className="w-full border border-slate-600 px-3 py-2 text-sm focus:outline-none focus:border-orange-500 bg-slate-900 text-white placeholder-slate-400" data-testid="cred-current-pw" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">New Username <span className="text-slate-400">(optional)</span></label>
                    <input value={credForm.new_username} onChange={(e) => setCredForm({ ...credForm, new_username: e.target.value })}
                      placeholder="Leave empty to keep current"
                      className="w-full border border-slate-600 px-3 py-2 text-sm focus:outline-none focus:border-orange-500 bg-slate-900 text-white placeholder-slate-400" data-testid="cred-new-username" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">New Password <span className="text-slate-400">(optional, min 6 chars)</span></label>
                    <input type="password" value={credForm.new_password} onChange={(e) => setCredForm({ ...credForm, new_password: e.target.value })}
                      placeholder="Leave empty to keep current"
                      className="w-full border border-slate-600 px-3 py-2 text-sm focus:outline-none focus:border-orange-500 bg-slate-900 text-white placeholder-slate-400" data-testid="cred-new-pw" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Confirm New Password</label>
                    <input type="password" value={credForm.confirm_password} onChange={(e) => setCredForm({ ...credForm, confirm_password: e.target.value })}
                      placeholder="Re-enter new password"
                      className="w-full border border-slate-600 px-3 py-2 text-sm focus:outline-none focus:border-orange-500 bg-slate-900 text-white placeholder-slate-400" data-testid="cred-confirm-pw" />
                  </div>
                </div>
                <button onClick={saveCredentials} disabled={credSaving || !credForm.current_password}
                  className="mt-4 flex items-center gap-1.5 bg-slate-900 hover:bg-slate-700 text-white px-5 py-2 text-sm font-bold uppercase tracking-wide transition-colors disabled:opacity-50"
                  data-testid="cred-save-btn">
                  <Save className="w-4 h-4" /> {credSaving ? "Saving..." : "Update Credentials"}
                </button>
              </div>

              {/* Sitemap */}
              <div className="bg-white border border-slate-200 p-5">
                <h3 className="font-bold text-slate-900 mb-3 text-sm uppercase tracking-wide flex items-center gap-2"><MapPinned className="w-4 h-4" /> XML Sitemap</h3>
                <p className="text-sm text-slate-600 mb-3">Auto-generated sitemap with all pages and published blog posts.</p>
                <div className="flex flex-wrap gap-3">
                  <a href={`${API}/sitemap.xml`} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1.5 border border-slate-300 hover:border-orange-500 text-slate-700 hover:text-orange-600 px-4 py-2 text-sm font-bold uppercase tracking-wide transition-colors"
                    data-testid="view-sitemap-btn">
                    <Eye className="w-4 h-4" /> View Sitemap
                  </a>
                  <a href={`${API}/sitemap-download`} download
                    className="flex items-center gap-1.5 bg-slate-900 hover:bg-slate-700 text-white px-4 py-2 text-sm font-bold uppercase tracking-wide transition-colors"
                    data-testid="download-sitemap-btn">
                    <Download className="w-4 h-4" /> Download sitemap.xml
                  </a>
                </div>
                <p className="text-xs text-slate-400 mt-2">Sitemap URL: <code className="text-orange-500">{API}/sitemap.xml</code></p>
              </div>
            </div>
          )}
        </main>
      </div>

      {/* Booking Detail Modal */}
      {selectedBooking && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="bg-slate-900 px-5 py-4 flex items-center justify-between">
              <h3 className="text-white font-bold uppercase" style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: "1.2rem" }}>
                Booking Details
              </h3>
              <button onClick={() => setSelectedBooking(null)} className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-3 text-sm">
              {[
                ["Full Name", selectedBooking.full_name],
                ["Company", selectedBooking.company_name || "—"],
                ["Phone", selectedBooking.phone],
                ["Email", selectedBooking.email],
                ["Plot Size", selectedBooking.plot_size],
                ["Industry Type", selectedBooking.industry_type],
                ["Payment Mode", selectedBooking.payment_mode],
                ["Address", selectedBooking.address],
                ["Message", selectedBooking.message || "—"],
                ["Submitted", selectedBooking.created_at?.slice(0, 19).replace("T", " ")],
                ["Status", selectedBooking.status],
                ["Payment", selectedBooking.payment_status || "pending"],
              ].map(([label, value]) => (
                <div key={label} className="flex gap-3 border-b border-slate-100 pb-2">
                  <span className="text-slate-500 font-medium w-32 flex-shrink-0">{label}:</span>
                  <span className="text-slate-800">{value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
